import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Locale } from '@ivario-arc/shared/src/types/locales';
import {
  SUPPORTED_LOCALES,
  markLanguagePendingSync,
  setStoredLanguage,
} from '@utils/language';

const LOCALE_LABELS: Record<Locale, string> = {
  [Locale.DE]: '🇩🇪 Deutsch',
  [Locale.EN]: '🇬🇧 English (UK)',
  [Locale.NL]: '🇳🇱 Nederlands',
};

export function LanguageConfirmOverlay({
  detectedLocale,
  onConfirm,
}: {
  detectedLocale: Locale;
  onConfirm: () => void;
}) {
  const { t, i18n } = useTranslation('view', { keyPrefix: 'landing.overlay' });
  const [selectedLocale, setSelectedLocale] = useState(detectedLocale);
  const tSelected = i18n.getFixedT(selectedLocale, 'view', 'landing.overlay');

  const confirmLanguage = (locale: Locale) => {
    setStoredLanguage(locale);
    markLanguagePendingSync(locale);
    void i18n.changeLanguage(locale);
    document.documentElement.lang = locale;
    onConfirm();
  };

  return (
    <div className="fixed inset-0 bg-dark/60 backdrop-blur-sm z-[100] flex items-center justify-center p-5 animate-[fadeIn_0.2s_ease]">
      <div className="bg-white rounded-[20px] px-8 py-9 max-w-[440px] w-full shadow-2xl animate-[slideUp_0.25s_cubic-bezier(.22,.68,0,1.2)]">
        <div className="text-[22px] font-extrabold text-dark mb-6">IVARIO</div>

        <div className="text-lg font-bold text-dark mb-2">{t('title')}</div>
        <p
          className="text-[15px] text-gray-600 leading-relaxed mb-6"
          dangerouslySetInnerHTML={{ __html: t('sub') }}
        />

        <button
          onClick={() => confirmLanguage(detectedLocale)}
          className="block w-full py-3.5 bg-ivarioYellow text-dark font-bold text-base rounded-[10px] border-none cursor-pointer transition-all hover:bg-ivarioYellow-hover active:scale-[0.98]"
        >
          {t('confirm')}
        </button>

        <div className="flex items-center gap-2.5 my-5">
          <div className="flex-1 h-px bg-grey2" />
          <span className="text-xs text-gray-400 whitespace-nowrap font-medium">
            {t('or')}
          </span>
          <div className="flex-1 h-px bg-grey2" />
        </div>

        <div className="text-[13px] text-gray-600 mb-2 font-medium">
          {t('otherLabel')}
        </div>
        <select
          value={selectedLocale}
          onChange={(e) => setSelectedLocale(e.target.value as Locale)}
          className="w-full py-3 px-3.5 border-2 border-grey2 rounded-[10px] font-semibold text-[15px] text-dark bg-white cursor-pointer appearance-none bg-no-repeat bg-[right_14px_center] bg-[length:12px_8px] pr-10 transition-colors focus:outline-none focus:border-ivarioBlue"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23718096' stroke-width='1.8' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
          }}
        >
          {SUPPORTED_LOCALES.map((locale) => (
            <option key={locale} value={locale}>
              {LOCALE_LABELS[locale]}
            </option>
          ))}
        </select>

        <button
          onClick={() => confirmLanguage(selectedLocale)}
          className="block w-full mt-2.5 py-3 bg-transparent text-ivarioBlue font-bold text-[15px] border-2 border-ivarioBlue rounded-[10px] cursor-pointer transition-all hover:bg-ivarioBlue hover:text-white active:scale-[0.98]"
        >
          {tSelected('selectBtn')}
        </button>
      </div>
    </div>
  );
}
